import { useState, useEffect, useCallback, useRef } from "react";

/**
 * お知らせの未読状態を管理するカスタムフック
 */
export const useUnreadNotice = ({ enabled = true } = {}) => {
  const [hasUnreadNotice, setHasUnreadNotice] = useState(false);

  const subscriptionRef = useRef(null);
  const consumerRef = useRef(null);

  // 現在の fetch を中断するための controller
  const abortControllerRef = useRef(null);

  // 最新のリクエストだけ反映するための連番
  const requestIdRef = useRef(0);

  /**
   * 未読状態をAPIから取得
   */
  const fetchUnreadNotice = useCallback(async () => {
    if (!enabled) return;

    // 前回のリクエストを中断
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const currentRequestId = ++requestIdRef.current;

    try {
      const res = await fetch("/api/notices/unread_status", {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
        signal: controller.signal,
      });

      if (!res.ok) throw new Error("未読取得に失敗しました");

      const data = await res.json();

      // 中断済みなら何もしない
      if (controller.signal.aborted) return;

      // 最新リクエスト以外は反映しない
      if (currentRequestId !== requestIdRef.current) return;

      setHasUnreadNotice(!!data.has_unread_notice);
    } catch (error) {
      // AbortError は正常系として握りつぶす
      if (error.name === "AbortError") return;

      console.error(error);
    } finally {
      // 自分自身が最新 controller のときだけ片付ける
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [enabled]);

  // 初回表示時に未読状態を取得
  useEffect(() => {
    if (!enabled) {
      setHasUnreadNotice(false);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      return;
    }

    fetchUnreadNotice();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [enabled, fetchUnreadNotice]);

  /**
   * Action Cableでお知らせ投稿通知を購読
   *
   * お知らせが投稿されたら通知を受け取り、
   * unread_status を再取得してサイドバーの ! を更新する
   */
  useEffect(() => {
    if (!enabled) return undefined;

    let isActive = true;

    const setupSubscription = async () => {
      const { createConsumer } = await import("@rails/actioncable");

      if (!isActive) return;

      const consumer = createConsumer();
      consumerRef.current = consumer;

      const subscription = consumer.subscriptions.create(
        { channel: "UnreadNoticesChannel" },
        {
          received(data) {
            if (data?.type === "notice_posted") {
              fetchUnreadNotice();
            }
          },
        },
      );

      subscriptionRef.current = subscription;
    };

    setupSubscription();

    return () => {
      isActive = false;

      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }

      if (consumerRef.current) {
        consumerRef.current.disconnect();
        consumerRef.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [enabled, fetchUnreadNotice]);

  return {
    hasUnreadNotice,
    setHasUnreadNotice,
    fetchUnreadNotice,
  };
};
